import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RARITY_CSS = {
  common:    { border: "border-zinc-700",   ring: "shadow-zinc-700/30",   chip: "bg-zinc-800 text-neutral-300" },
  rare:      { border: "border-purple-500", ring: "shadow-purple-500/40", chip: "bg-purple-900/40 text-purple-300" },
  legendary: { border: "border-[#D4AF37]",  ring: "shadow-[#D4AF37]/50",  chip: "bg-[#D4AF37]/10 text-[#D4AF37]" },
};

export default function StickerCollection() {
  const { user, refreshUser } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [owned, setOwned] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const [activeSet, setActiveSet] = useState("phoenix");

  const isPremium = !!user?.is_premium || user?.role === "owner";

  const load = async () => {
    try {
      const [c, me] = await Promise.all([
        axios.get(`${API}/stickers/catalog`),
        user ? axios.get(`${API}/stickers/me`) : Promise.resolve({ data: { owned: [], pinned: [] } }),
      ]);
      setCatalog(c.data);
      setOwned(me.data.owned || []);
      setPinned(me.data.pinned || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const buy = async (sticker_id) => {
    const sticker = catalog?.stickers?.find((s) => s.id === sticker_id);
    const ok = await confirmDialog({
      title: `Unlock sticker?`,
      message: `${sticker?.icon || "🎨"} ${sticker?.name}
Cost: ${sticker?.cost_pt} Phoenix Tokens. Permanent unlock — pin up to ${catalog?.max_pinned || 3} to show on your profile.`,
      confirmLabel: `Buy — ${sticker?.cost_pt} Phoenix Tokens`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBuyingId(sticker_id);
    try {
      const { data } = await axios.post(`${API}/stickers/buy`, { sticker_id });
      setOwned(data.owned || []);
      const sticker = catalog?.stickers?.find((s) => s.id === sticker_id);
      toast.success(`${sticker?.icon || "🎨"} ${sticker?.name} unlocked!`, { duration: 4000 });
      if (data.new_badges?.length) {
        data.new_badges.forEach((id, i) => {
          setTimeout(() => {
            toast.success(`🏅 New badge unlocked!`, { description: id, duration: 4500 });
          }, (i + 1) * 700);
        });
      }
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setBuyingId(null);
    }
  };

  const togglePin = async (sticker_id) => {
    let next;
    if (pinned.includes(sticker_id)) {
      next = pinned.filter((p) => p !== sticker_id);
    } else {
      if (pinned.length >= (catalog?.max_pinned || 3)) {
        toast.info(`You can only pin ${catalog?.max_pinned || 3} stickers. Unpin one first.`);
        return;
      }
      next = [...pinned, sticker_id];
    }
    setPinned(next);
    try {
      await axios.post(`${API}/stickers/pin`, { pinned: next });
    } catch (e) {
      toast.error("Pin update failed.");
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0F0F0F] border border-zinc-800 p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (!catalog) return null;

  const sets = catalog.sets || [];
  const stickers = catalog.stickers || [];
  const setStickers = stickers.filter((s) => s.set === activeSet);
  const ownedInSet = setStickers.filter((s) => owned.includes(s.id)).length;
  const setObj = sets.find((s) => s.id === activeSet);

  return (
    <div
      data-testid="sticker-collection"
      className="bg-[#0F0F0F] border border-zinc-800 p-6"
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Sticker Collection 🎨
          </div>
          <h3 className="font-display text-2xl text-white">
            {owned.length} <span className="text-neutral-500 text-lg">/ {stickers.length}</span> collected
          </h3>
        </div>
        <div className="text-xs text-neutral-400 max-w-xs">
          Complete a set → <span className="text-[#D4AF37]">+{catalog.set_bonus_pt} Phoenix Tokens</span>. Collect all 6 → <span className="text-[#D4AF37]">+{catalog.sage_bonus_pt} Phoenix Tokens</span> + Sticker Sage 🏆
        </div>
      </div>

      {/* Set tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {sets.map((s) => {
          const setStickerIds = stickers.filter((st) => st.set === s.id).map((st) => st.id);
          const inSet = setStickerIds.filter((id) => owned.includes(id)).length;
          const total = setStickerIds.length;
          const complete = inSet === total;
          return (
            <button
              key={s.id}
              data-testid={`sticker-set-tab-${s.id}`}
              onClick={() => setActiveSet(s.id)}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border-2 transition-colors flex items-center gap-2 ${
                activeSet === s.id
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                  : "border-zinc-800 text-neutral-300 hover:border-zinc-600"
              } ${complete ? "ring-2 ring-[#D4AF37]/40" : ""}`}
            >
              <span>{s.icon}</span>
              {s.name}
              <span className={`text-[10px] ${complete ? "text-[#D4AF37]" : "text-neutral-500"}`}>
                {inSet}/{total}
              </span>
            </button>
          );
        })}
      </div>

      {/* Set progress bar */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] uppercase tracking-widest text-neutral-500">
            {setObj?.name} progress
          </span>
          <span className="text-xs font-bold text-[#D4AF37]">
            {ownedInSet}/{setStickers.length}
          </span>
        </div>
        <div className="h-2 bg-zinc-900">
          <div
            className="h-2 bg-gradient-to-r from-purple-500 via-[#D4AF37] to-red-500 transition-all"
            style={{ width: `${(ownedInSet / Math.max(1, setStickers.length)) * 100}%` }}
          />
        </div>
      </div>

      {/* Sticker grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {setStickers.map((s) => {
          const isOwned = owned.includes(s.id);
          const isPinned = pinned.includes(s.id);
          const isLocked = !!s.premium_only && !isPremium;
          const isBuying = buyingId === s.id;
          const css = RARITY_CSS[s.rarity] || RARITY_CSS.common;
          return (
            <div
              key={s.id}
              data-testid={`sticker-${s.id}`}
              className={`relative border-2 ${css.border} p-3 transition-all ${
                isOwned
                  ? `bg-[#0A0A0A] ${css.ring} shadow-lg`
                  : "bg-[#0A0A0A]/50 opacity-60 grayscale"
              }`}
            >
              {isPinned && (
                <span className="absolute top-1 right-1 text-[9px] uppercase tracking-widest text-[#D4AF37]">
                  📌
                </span>
              )}
              <div className="text-5xl text-center my-3 leading-none">{s.icon}</div>
              <div className="text-center text-white font-display text-sm leading-tight mb-1">
                {s.name}
              </div>
              <div className="text-center mb-3">
                <span className={`inline-block px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${css.chip}`}>
                  {s.rarity}
                </span>
              </div>
              {isOwned ? (
                <button
                  onClick={() => togglePin(s.id)}
                  data-testid={`sticker-pin-${s.id}`}
                  className={`w-full text-[10px] uppercase tracking-widest font-bold py-2 border transition-colors ${
                    isPinned
                      ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                      : "border-zinc-800 text-neutral-400 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                >
                  <Check size={10} className="inline mr-1" />
                  {isPinned ? "Pinned" : "Pin"}
                </button>
              ) : isLocked ? (
                <div className="w-full text-[10px] uppercase tracking-widest font-bold py-2 border border-zinc-800 text-neutral-500 text-center flex items-center justify-center gap-1">
                  <Lock size={10} /> Premium
                </div>
              ) : (
                <button
                  data-testid={`sticker-buy-${s.id}`}
                  onClick={() => buy(s.id)}
                  disabled={isBuying || !user}
                  className="w-full text-[10px] uppercase tracking-widest font-bold py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isBuying ? <Loader2 size={10} className="inline animate-spin" /> : `${s.cost_pt} Phoenix Tokens`}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!user && (
        <p className="mt-5 text-center text-xs text-neutral-500">
          <Lock size={10} className="inline mr-1" />
          Sign in to collect stickers and complete sets.
        </p>
      )}
    </div>
  );
}
